/**
 * KI-Budget-Karte (Design-System 2026-06): Monatsbudget prominent in PROZENT
 * (kein Euro-Betrag mehr), darunter 6 Std / Woche dezent. Daten: GET /api/quota.
 */
import { useEffect } from "preact/hooks";
import { quotaWindows, quotaError, loadQuota } from "../state/quota";
import { IconSparkles } from "../app/icons";
import type { QuotaWindow } from "../api/types";

function pct(w: QuotaWindow): number {
  const limit = Number(w.limit) || 0;
  const used = Number(w.used) || 0;
  return limit > 0 ? Math.min(100, Math.max(0, Math.round((used / limit) * 100))) : 0;
}

/** Überschritten aus ROHwerten (nicht aus gerundetem Prozent — sonst kippt 99,6 % auf „voll"). */
function exceededOf(w: QuotaWindow): boolean {
  const limit = Number(w.limit) || 0;
  const used = Number(w.used) || 0;
  return !!w.exceeded || (limit > 0 && used >= limit);
}

function shortLabel(w: QuotaWindow): string {
  if (w.key === "6h") return "6 Std";
  if (w.key === "week") return "Woche";
  return w.label || w.key;
}

export function QuotaCard() {
  const windows = quotaWindows.value;
  const error = quotaError.value;

  useEffect(() => {
    loadQuota(true);
  }, []);

  if (error || !windows || !windows.length) {
    const msg = error
      ? "Budget konnte nicht geladen werden."
      : !windows
        ? "Wird geladen …"
        : "Keine Budgetdaten verfügbar.";
    return (
      <div class="bud">
        <div class="bud-h"><IconSparkles size={15} /><b>Mein KI-Budget</b></div>
        <p class="bud-msg">{msg}</p>
      </div>
    );
  }

  // „Diesen Monat" NUR aus dem echten Monatsfenster (key==="month"); sonst keine
  // Falsch-Beschriftung — dann zeigen wir nur die vorhandenen Fenster als Mini.
  const month = windows.find((w) => w.key === "month") || null;
  const minis = windows.filter((w) => w.key !== "month");
  const mPct = month ? pct(month) : 0;
  const mExceeded = month ? exceededOf(month) : false;
  let frei = "";
  if (month?.reset_at) {
    try {
      frei = new Date(month.reset_at).toLocaleDateString("de-DE", { day: "numeric", month: "long" });
    } catch {
      frei = "";
    }
  }

  return (
    <div class="bud">
      <div class="bud-h"><IconSparkles size={15} /><b>Mein KI-Budget</b></div>
      {month ? (
        <div class="bud-month">
          <div class={`bud-ln${mExceeded ? " exceeded" : ""}`}>
            <span class="l">Diesen Monat</span>
            <span class="v">{mPct} % genutzt</span>
          </div>
          <div class="bud-bar"><i class={mExceeded ? "exceeded" : ""} style={`width:${mPct}%`} /></div>
          {frei ? <div class="bud-free">frei ab {frei}</div> : null}
        </div>
      ) : null}
      {minis.length ? (
        <div class="bud-mini">
          {minis.map((w) => {
            const p = pct(w);
            return (
              <div key={w.key}>
                <div class="bud-ln"><span class="l">{shortLabel(w)}</span><span class="v">{p} %</span></div>
                <div class="bud-bar"><i style={`width:${p}%`} /></div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
